import { useState } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { formatBRL } from '@lira/shared';
import { ChartTooltip } from './ChartTooltip';

const EASE = [0.16, 1, 0.3, 1] as const;
const VBW = 320;
const VBH = 160;
const PL = 10;
const PR = 10;
const PT = 16;
const PB = 28;
const PLOT_W = VBW - PL - PR;
const PLOT_H = VBH - PT - PB;
const Y_BOTTOM = PT + PLOT_H;

export interface LinePoint {
  label: string;
  /** valor em centavos (inteiro). */
  value: number;
}

interface LineChartProps {
  points: LinePoint[];
}

interface XY {
  x: number;
  y: number;
}

/** Linha suavizada (Catmull-Rom → Bézier cúbica). */
function smoothPath(pts: XY[]): string {
  const n = pts.length;
  if (n === 0) return '';
  const first = pts[0]!;
  if (n === 1) return `M ${first.x} ${first.y}`;
  let d = `M ${first.x} ${first.y}`;
  for (let i = 0; i < n - 1; i++) {
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p0 = pts[i - 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

/**
 * Linha de evolução do saldo (N pontos): traço roxo suavizado que desenha via
 * pathLength, área de preenchimento leve, pontos nos vértices e labels de mês.
 * viewBox 0 0 320 160. Robusto a vazio / valores iguais (sem divisão por zero).
 */
export function LineChart({ points }: LineChartProps) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<number | null>(null);
  const n = points.length;

  if (n === 0) {
    return (
      <div className="grid h-40 place-items-center text-sm text-muted">Sem dados</div>
    );
  }

  const values = points.map((p) => p.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    // linha plana → centraliza verticalmente
    min -= 1;
    max += 1;
  }

  const xAt = (i: number): number => (n <= 1 ? PL + PLOT_W / 2 : PL + (i / (n - 1)) * PLOT_W);
  const yAt = (v: number): number => PT + (1 - (v - min) / (max - min)) * PLOT_H;

  const coords: XY[] = points.map((p, i) => ({ x: xAt(i), y: yAt(p.value) }));
  const line = smoothPath(coords);
  const firstX = coords[0]!.x;
  const lastX = coords[n - 1]!.x;
  const area = n === 1 ? '' : `${line} L ${lastX} ${Y_BOTTOM} L ${firstX} ${Y_BOTTOM} Z`;

  return (
    <div className="relative">
    <svg
      viewBox={`0 0 ${VBW} ${VBH}`}
      className="w-full"
      role="img"
      aria-label="Balanço de receitas e despesas nos últimos meses"
    >
      {area && (
        <m.path
          d={area}
          fill="var(--color-primary)"
          fillOpacity={0.1}
          stroke="none"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduce ? 0 : 0.5, delay: reduce ? 0 : 0.2, ease: EASE }}
        />
      )}

      {n > 1 && (
        <m.path
          d={line}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: reduce ? 0 : 0.8, ease: EASE }}
        />
      )}

      {coords.map((c, i) => (
        <m.circle
          key={i}
          cx={c.x}
          cy={c.y}
          r={active === i ? 5 : 3}
          fill={active === i ? 'var(--color-primary)' : 'var(--color-surface)'}
          stroke="var(--color-primary)"
          strokeWidth={2}
          className="[transition:r_.15s_ease,fill_.15s_ease]"
          initial={reduce ? false : { scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          transition={{
            duration: reduce ? 0 : 0.2,
            delay: reduce ? 0 : 0.5 + i * 0.05,
            ease: EASE,
          }}
        >
          <title>{`${points[i]!.label} — ${formatBRL(points[i]!.value)}`}</title>
        </m.circle>
      ))}

      {points.map((p, i) => (
        <text
          key={i}
          x={xAt(i)}
          y={VBH - 8}
          textAnchor="middle"
          className="fill-muted"
          fontSize={10}
        >
          {p.label}
        </text>
      ))}

      {/* alvos de hover invisíveis e largos (facilitam apontar o ponto) */}
      {coords.map((c, i) => (
        <circle
          key={`hit${i}`}
          cx={c.x}
          cy={c.y}
          r={14}
          fill="transparent"
          tabIndex={0}
          role="img"
          aria-label={`${points[i]!.label}: ${formatBRL(points[i]!.value)}`}
          className="cursor-pointer outline-none"
          onMouseEnter={() => setActive(i)}
          onMouseLeave={() => setActive((cur) => (cur === i ? null : cur))}
          onFocus={() => setActive(i)}
          onBlur={() => setActive((cur) => (cur === i ? null : cur))}
        />
      ))}
    </svg>

      {active !== null && (
        <ChartTooltip
          xPct={(coords[active]!.x / VBW) * 100}
          yPct={(coords[active]!.y / VBH) * 100}
          color="var(--color-primary)"
          label={points[active]!.label}
          value={formatBRL(points[active]!.value)}
        />
      )}
    </div>
  );
}
